import { Result } from "./Result";

/* This is a tiny wrapper around `fetch` function that never throws and returns
 * an `Result` type instead, which represents either a failure or a success.
 *
 * Use whatever you prefer.
 */

export class NetworkError extends Error {

	cause: TypeError;

	constructor(cause: TypeError) {
		super(cause.message);
		this.cause = cause;
	}
}

interface ResponseOverrides {
	arrayBuffer(): Promise<Result<ArrayBuffer, RangeError>>;
	blob(): Promise<Result<Blob>>;
	formData(): Promise<Result<FormData, TypeError>>;
	json(): Promise<Result<unknown, SyntaxError>>;
	text(): Promise<Result<string>>;
}

export interface Response extends Omit<globalThis.Response, keyof ResponseOverrides>, ResponseOverrides { }

const handler: ProxyHandler<globalThis.Response> = {
	get: (target, prop) => {
		switch (prop) {
			case "arrayBuffer":
			case "formData":
			case "json":
				return () => Result.tryPromise(target[prop]());
			case "blob":
			case "text":
				return () => (target[prop]() as Promise<Blob | string>).then(Result.success);
			default:
				return Reflect.get(target, prop, target);
		}
	}
};

export function fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Result<Response, NetworkError>> {
	return globalThis.fetch(input, init).then(
		(res) => Result.success(new Proxy(res, handler) as unknown as Response),
		(error) => Result.failure(new NetworkError(error as TypeError)),
	);
}
